import {
  Customer,
  Contract,
  InventoryItem,
  Order,
  Invoice,
  Payment,
  Delivery,
  PriceData,
  ComplianceRecord,
  SalesMetrics,
  VoiceInvoiceData,
  ApiResponse,
  PaginatedResponse,
  CustomerFilters,
  OrderFilters,
  InventoryFilters,
  SalesDashboardSummary,
  DateRange
} from '../types';
import { getEnvironmentConfig } from '../../../config/environment';

// Storage keys
const CONTRACTS_STORAGE_KEY = 'saubhagya_contracts';
const STORAGE_KEY = 'saubhagya_customers'; // Legacy key for customers (deprecated - use API only)

// Initialize contracts storage on module load
const initializeContractsStorage = (): void => {
  if (typeof window === 'undefined') return; // Skip in SSR

  try {
    // Clean contracts storage - remove any mock contracts like SB-2024-001
    try {
      const contractsStored = localStorage.getItem(CONTRACTS_STORAGE_KEY);
      if (contractsStored) {
        const contracts = JSON.parse(contractsStored);
        const filteredContracts = contracts.filter(
          (c: any) => !c.contractNumber.startsWith('SB-2024-')
        );
        localStorage.setItem(CONTRACTS_STORAGE_KEY, JSON.stringify(filteredContracts));
        console.log('Cleaned contracts storage:', { original: contracts.length, cleaned: filteredContracts.length });
      }
    } catch (parseError) {
      // If parse fails, just clear the contracts storage
      localStorage.setItem(CONTRACTS_STORAGE_KEY, JSON.stringify([]));
      console.log('Reset contracts storage due to parse error');
    }
  } catch (error) {
    console.error('Error initializing contracts storage:', error);
  }
};

// Get contracts from localStorage
const getStoredContracts = (): Contract[] => {
  if (typeof window === 'undefined') return generateMockContracts(); // Skip in SSR

  try {
    const stored = localStorage.getItem(CONTRACTS_STORAGE_KEY);
    if (stored) {
      const contracts = JSON.parse(stored);
      // Convert date strings back to Date objects
      return contracts.map((c: any) => ({
        ...c,
        startDate: new Date(c.startDate),
        endDate: new Date(c.endDate),
        signedDate: c.signedDate ? new Date(c.signedDate) : undefined
      }));
    }
  } catch (error) {
    console.error('Error reading contracts from localStorage:', error);
  }
  return generateMockContracts();
};

// Save contracts to localStorage
const saveStoredContracts = (contracts: Contract[]): void => {
  if (typeof window === 'undefined') return; // Skip in SSR

  try {
    localStorage.setItem(CONTRACTS_STORAGE_KEY, JSON.stringify(contracts));
    console.log('Contracts saved to localStorage:', contracts.length);
  } catch (error) {
    console.error('Error saving contracts to localStorage:', error);
  }
};

// Initialize contracts storage on module load
initializeContractsStorage();

// Mock data generators (deprecated - use API instead)
// Customers should come from API only, not from localStorage

const generateMockContracts = (): Contract[] => [];

// Removed generateMockInvoices - invoices should come from API with real company banking details

// Service functions
export const salesService = {
  // ====== SALES UNIT FILTERING METHODS ======

  /**
   * Get current user's assigned sales units
   * Returns list of sales unit IDs the user has access to
   */
  async getCurrentUserSalesUnits(): Promise<{ success: boolean; salesUnitIds: number[]; totalUnits: number; error?: string }> {
    try {
      const config = getEnvironmentConfig();
      const API_BASE_URL = config.microservices.commerce;
      const token = localStorage.getItem('saubhagya_jwt_token') || '';

      console.log('📊 Fetching current user\'s assigned sales units...');

      const response = await fetch(
        `${API_BASE_URL}/api/v1/sales-access/my-sales-units`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        console.warn('⚠️ Failed to fetch sales units:', response.status);
        return { success: false, salesUnitIds: [], totalUnits: 0, error: `HTTP ${response.status}` };
      }

      const data = await response.json();
      console.log('✅ User sales units fetched:', data.salesUnitIds);
      return {
        success: data.success,
        salesUnitIds: data.salesUnitIds || [],
        totalUnits: data.totalUnits || 0
      };
    } catch (error) {
      console.error('❌ Error fetching sales units:', error);
      return { success: false, salesUnitIds: [], totalUnits: 0, error: String(error) };
    }
  },

  /**
   * Get current user ID from localStorage
   */
  getCurrentUserId(): number | null {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || null;
      }
    } catch (error) {
      console.error('Error getting current user ID:', error);
    }
    return null;
  },

  /**
   * Check if user is ADMIN (can see all data)
   */
  isCurrentUserAdmin(): boolean {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.role === 'ADMIN' || user.role === 'SUPER_ADMIN' || user.roles?.includes('ADMIN');
      }
    } catch (error) {
      console.error('Error checking admin role:', error);
    }
    return false;
  },

  // ====== CUSTOMER MANAGEMENT ======
  // Customer Management
  async getCustomers(filters?: CustomerFilters): Promise<PaginatedResponse<Customer>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      // Fetch from backend API
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded customers from backend API');

        // Transform backend Buyer responses to frontend Customer format
        if (data.data && Array.isArray(data.data)) {
          let customers: Customer[] = data.data.map((buyer: any) => ({
            id: buyer.id,
            name: buyer.companyName,
            type: buyer.buyerType === 'B2B' ? 'industrial' : 'commercial',
            contactPerson: buyer.contactPersonName,
            phone: buyer.primaryPhone,
            email: buyer.email,
            address: {
              street: buyer.registeredAddress?.split(',')[0]?.trim() || '',
              city: buyer.registeredAddress?.split(',')[1]?.trim() || '',
              state: buyer.registeredAddress?.split(',')[2]?.trim() || '',
              pincode: buyer.registeredAddress?.split(',')[3]?.trim() || '',
              coordinates: { lat: 28.7041, lng: 77.1025 }
            },
            creditLimit: buyer.creditLimit || 0,
            creditUsed: buyer.creditUsed || 0,
            paymentTerms: buyer.paymentTerms ? parseInt(buyer.paymentTerms.split('_')[1]) : 30,
            gstNumber: buyer.gstNumber || '',
            panNumber: buyer.panNumber || '',
            status: buyer.onboardingStatus === 'APPROVED' ? 'active' : 'inactive',
            registrationDate: new Date(),
            lastOrderDate: new Date(),
            totalOrders: 0,
            totalRevenue: 0,
            riskCategory: 'medium'
          }));

          // Apply filters if provided
          if (filters) {
            if (filters.type && filters.type.length > 0) {
              customers = customers.filter(c => filters.type!.includes(c.type));
            }
            if (filters.status && filters.status.length > 0) {
              customers = customers.filter(c => filters.status!.includes(c.status));
            }
            if (filters.riskCategory && filters.riskCategory.length > 0) {
              customers = customers.filter(c => filters.riskCategory!.includes(c.riskCategory));
            }
            if (filters.city && filters.city.length > 0) {
              customers = customers.filter(c => filters.city!.includes(c.address.city));
            }
          }

          console.log('Filtered customers:', customers.length, 'Applied filters:', filters);

          return {
            success: true,
            data: customers,
            pagination: {
              page: data.pagination?.page || 1,
              limit: data.pagination?.limit || 10,
              total: data.pagination?.total || customers.length,
              totalPages: data.pagination?.totalPages || 1
            }
          };
        }
      }

      // API returned non-200 status
      throw new Error(`API returned status ${response.status}`);
    } catch (error) {
      console.error('❌ Failed to fetch customers from backend API:', error);
      // Return error response - do not fall back to mock data
      return {
        success: false,
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        },
        message: `Failed to load customers: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  },

  async getCustomerById(id: string): Promise<ApiResponse<Customer>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers/${id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const buyer = data.data || data;

        const customer: Customer = {
          id: buyer.id,
          name: buyer.companyName,
          type: buyer.buyerType === 'B2B' ? 'industrial' : 'commercial',
          contactPerson: buyer.contactPersonName,
          phone: buyer.primaryPhone,
          email: buyer.email,
          address: {
            street: buyer.registeredAddress?.split(',')[0]?.trim() || '',
            city: buyer.registeredAddress?.split(',')[1]?.trim() || '',
            state: buyer.registeredAddress?.split(',')[2]?.trim() || '',
            pincode: buyer.registeredAddress?.split(',')[3]?.trim() || '',
            coordinates: { lat: 28.7041, lng: 77.1025 }
          },
          creditLimit: buyer.creditLimit || 0,
          creditUsed: buyer.creditUsed || 0,
          paymentTerms: buyer.paymentTerms ? parseInt(buyer.paymentTerms.split('_')[1]) : 30,
          gstNumber: buyer.gstNumber || '',
          panNumber: buyer.panNumber || '',
          status: buyer.onboardingStatus === 'APPROVED' ? 'active' : 'inactive',
          registrationDate: new Date(),
          lastOrderDate: new Date(),
          totalOrders: 0,
          totalRevenue: 0,
          riskCategory: 'medium'
        };

        return {
          success: true,
          data: customer,
          message: 'Customer found'
        };
      }

      throw new Error(`API returned status ${response.status}`);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      return {
        success: false,
        data: null as any,
        message: error instanceof Error ? error.message : 'Customer not found'
      };
    }
  },

  async createCustomer(customer: Omit<Customer, 'id'>): Promise<ApiResponse<Customer>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      // Map frontend customer format to backend CreateBuyerRequest format
      const buyerRequest = {
        type: customer.type === 'industrial' ? 'B2B' : 'B2C',
        name: customer.name,
        contactPerson: customer.contactPerson,
        phone: customer.phone,
        email: customer.email,
        address: customer.address,
        gstNumber: customer.gstNumber || '',
        panNumber: customer.panNumber || '',
        creditLimit: customer.creditLimit || 0,
        paymentTerms: customer.paymentTerms || 30
      };

      console.log('[createCustomer] Sending to backend:', buyerRequest);

      // Call backend API to create customer
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(buyerRequest)
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Customer created successfully via backend API:', data);

        // Transform backend Buyer response back to frontend Customer format
        const backendBuyer = data.data || data;
        const newCustomer: Customer = {
          id: backendBuyer.id || `cust_${Date.now()}`,
          name: backendBuyer.companyName,
          type: backendBuyer.buyerType === 'B2B' ? 'industrial' : 'commercial',
          contactPerson: backendBuyer.contactPersonName,
          phone: backendBuyer.primaryPhone,
          email: backendBuyer.email,
          address: {
            street: backendBuyer.registeredAddress?.split(',')[0]?.trim() || '',
            city: backendBuyer.registeredAddress?.split(',')[1]?.trim() || '',
            state: backendBuyer.registeredAddress?.split(',')[2]?.trim() || '',
            pincode: backendBuyer.registeredAddress?.split(',')[3]?.trim() || '',
            coordinates: { lat: 28.7041, lng: 77.1025 }
          },
          creditLimit: backendBuyer.creditLimit || customer.creditLimit || 0,
          creditUsed: backendBuyer.creditUsed || 0,
          paymentTerms: customer.paymentTerms || 30,
          gstNumber: backendBuyer.gstNumber || customer.gstNumber || '',
          panNumber: backendBuyer.panNumber || customer.panNumber || '',
          status: 'active',
          registrationDate: new Date(),
          lastOrderDate: new Date(),
          totalOrders: 0,
          totalRevenue: 0,
          riskCategory: 'medium'
        };

        // Clear localStorage customers cache to force reload from API
        try {
          localStorage.removeItem(STORAGE_KEY);
          console.log('Cleared customers cache - will reload from API');
        } catch (e) {
          console.warn('Could not clear localStorage:', e);
        }

        return {
          success: true,
          data: newCustomer,
          message: 'Customer created successfully'
        };
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create customer' }));
        console.error('❌ Backend API error:', errorData);
        throw new Error(errorData.message || `HTTP Error: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error creating customer via backend API:', error);
      return {
        success: false,
        data: null as any,
        message: error instanceof Error ? error.message : 'Failed to create customer'
      };
    }
  },

  async updateCustomer(id: string, updates: Partial<Customer>): Promise<ApiResponse<Customer>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(updates)
        }
      );

      if (response.ok) {
        const data = await response.json();
        const buyer = data.data || data;

        const updatedCustomer: Customer = {
          id: buyer.id,
          name: buyer.companyName,
          type: buyer.buyerType === 'B2B' ? 'industrial' : 'commercial',
          contactPerson: buyer.contactPersonName,
          phone: buyer.primaryPhone,
          email: buyer.email,
          address: {
            street: buyer.registeredAddress?.split(',')[0]?.trim() || '',
            city: buyer.registeredAddress?.split(',')[1]?.trim() || '',
            state: buyer.registeredAddress?.split(',')[2]?.trim() || '',
            pincode: buyer.registeredAddress?.split(',')[3]?.trim() || '',
            coordinates: { lat: 28.7041, lng: 77.1025 }
          },
          creditLimit: buyer.creditLimit || 0,
          creditUsed: buyer.creditUsed || 0,
          paymentTerms: buyer.paymentTerms ? parseInt(buyer.paymentTerms.split('_')[1]) : 30,
          gstNumber: buyer.gstNumber || '',
          panNumber: buyer.panNumber || '',
          status: buyer.onboardingStatus === 'APPROVED' ? 'active' : 'inactive',
          registrationDate: new Date(),
          lastOrderDate: new Date(),
          totalOrders: 0,
          totalRevenue: 0,
          riskCategory: 'medium'
        };

        return {
          success: true,
          data: updatedCustomer,
          message: 'Customer updated successfully'
        };
      }

      throw new Error(`API returned status ${response.status}`);
    } catch (error) {
      console.error('Failed to update customer:', error);
      return {
        success: false,
        data: null as any,
        message: error instanceof Error ? error.message : 'Failed to update customer'
      };
    }
  },

  async checkEmailUniquenessForUpdate(email: string, customerId: string): Promise<boolean> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers?email=${encodeURIComponent(email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const customers = data.data || [];
        const existingCustomer = customers.find(
          (c: any) => c.email.toLowerCase() === email.toLowerCase() && c.id !== customerId
        );
        return !existingCustomer;
      }
      return true;
    } catch (error) {
      console.warn('Failed to check email uniqueness:', error);
      return true;
    }
  },

  async checkPhoneUniquenessForUpdate(phone: string, customerId: string): Promise<boolean> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers?phone=${encodeURIComponent(phone)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const customers = data.data || [];
        const existingCustomer = customers.find(
          (c: any) => c.primaryPhone === phone && c.id !== customerId
        );
        return !existingCustomer;
      }
      return true;
    } catch (error) {
      console.warn('Failed to check phone uniqueness:', error);
      return true;
    }
  },

  async checkGstUniquenessForUpdate(gst: string, customerId: string): Promise<boolean> {
    if (!gst) return true; // GST is optional

    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers?gst=${encodeURIComponent(gst)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const customers = data.data || [];
        const existingCustomer = customers.find(
          (c: any) => c.gstNumber === gst && c.id !== customerId
        );
        return !existingCustomer;
      }
      return true;
    } catch (error) {
      console.warn('Failed to check GST uniqueness:', error);
      return true;
    }
  },

  // Contract Management
  async getContracts(): Promise<PaginatedResponse<Contract>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      // Try to fetch from backend API first
      const response = await fetch(
        `${API_BASE_URL}/api/v1/contracts`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded contracts from backend API');

        // Check if response has data array
        if (data.data && Array.isArray(data.data)) {
          return {
            success: true,
            data: data.data,
            pagination: {
              page: data.pagination?.page || 1,
              limit: data.pagination?.limit || 10,
              total: data.pagination?.total || data.data.length,
              totalPages: data.pagination?.totalPages || 1
            }
          };
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to fetch contracts from backend API, falling back to localStorage:', error);
    }

    // Fallback to localStorage if API call fails
    const contracts = getStoredContracts();
    return {
      success: true,
      data: contracts,
      pagination: {
        page: 1,
        limit: 10,
        total: contracts.length,
        totalPages: 1
      }
    };
  },

  async getContractById(id: string): Promise<ApiResponse<Contract>> {
    const contracts = getStoredContracts();
    const contract = contracts.find(c => c.id === id);
    return {
      success: !!contract,
      data: contract!,
      message: contract ? 'Contract found' : 'Contract not found'
    };
  },

  async createContract(contract: Omit<Contract, 'id'>): Promise<ApiResponse<Contract>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      // Call backend API to create contract
      const response = await fetch(
        `${API_BASE_URL}/api/v1/contracts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(contract)
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Contract created successfully via backend API:', data);

        // Clear localStorage contracts cache to force reload from API
        try {
          localStorage.removeItem(CONTRACTS_STORAGE_KEY);
          console.log('Cleared contracts cache - will reload from API');
        } catch (e) {
          console.warn('Could not clear localStorage:', e);
        }

        return {
          success: true,
          data: data.data || data,
          message: 'Contract created successfully'
        };
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create contract' }));
        console.error('❌ Backend API error:', errorData);
        throw new Error(errorData.message || `HTTP Error: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error creating contract via backend API:', error);
      // Return error response instead of falling back to localStorage
      return {
        success: false,
        data: null as any,
        message: error instanceof Error ? error.message : 'Failed to create contract'
      };
    }
  },

  async updateContract(id: string, updates: Partial<Contract>): Promise<ApiResponse<Contract>> {
    const contracts = getStoredContracts();
    const contractIndex = contracts.findIndex(c => c.id === id);

    if (contractIndex === -1) {
      return { success: false, data: null as any, message: 'Contract not found' };
    }

    const updatedContract = { ...contracts[contractIndex], ...updates };

    // Update in contracts array
    contracts[contractIndex] = updatedContract;

    // Save to localStorage (persists across page refreshes)
    saveStoredContracts(contracts);
    console.log('Contract updated and persisted to localStorage:', updatedContract);

    return {
      success: true,
      data: updatedContract,
      message: 'Contract updated successfully'
    };
  },

  // Inventory Management
  /**
   * Fetch ONLY ready-to-sale batches from Biogas Service
   *
   * Data Source: biogas_batch_management table (status: READY_FOR_SALE)
   * All batches returned are automatically created from biogas production system
   * NO FALLBACK - Returns empty array if API fails
   */
  async getInventory(filters?: InventoryFilters): Promise<PaginatedResponse<InventoryItem>> {
    // Use centralized environment configuration
    const config = getEnvironmentConfig();
    const COMMERCE_API_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      console.log('📦 Fetching READY_FOR_SALE batches from mfg_production_batch table...');

      // Fetch ONLY ready-to-sale batches from mfg-production-batches endpoint
      const response = await fetch(
        `${COMMERCE_API_URL}/api/v1/mfg-production-batches/ready-for-sale`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch batches: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Successfully fetched READY_FOR_SALE batches:', data);

      // Handle response structure: { success: true, data: [...], message: "..." }
      const batchesArray = data?.data || data || [];

      if (batchesArray && Array.isArray(batchesArray) && batchesArray.length > 0) {
        const inventory: InventoryItem[] = await Promise.all(batchesArray.map(async (batch: any) => {
          // Map quality grades: PREMIUM→A, STANDARD→B, BASIC→C
          // Handle both string values and numeric codes
          let qualityGrade: 'A' | 'B' | 'C' = 'B';
          if (batch.qualityGrade) {
            const gradeStr = String(batch.qualityGrade).toUpperCase();
            if (gradeStr === 'PREMIUM' || gradeStr === '1') qualityGrade = 'A';
            else if (gradeStr === 'STANDARD' || gradeStr === '2') qualityGrade = 'B';
            else qualityGrade = 'C';
          }

          // Map purity from methane content
          const purity = batch.methaneContentPercent || 90;

          // Get quantity from outputQuantityKg (manufacturing batches use kg)
          const quantityKg = batch.outputQuantityKg || batch.biogasVolumeCubicMeters || 0;

          // Get product name from batch - using finalProductTypeName field from API
          // This contains actual product names like "Funeral Logs", "Dung Sticks", etc.
          // Priority: finalProductTypeName > productName > dungProductName > default
          let productType = 'Biogas';

          if (batch.finalProductTypeName && batch.finalProductTypeName.trim() && batch.finalProductTypeName !== 'Unknown') {
            // Use actual product name from finalProductTypeName field
            productType = batch.finalProductTypeName;
          } else if (batch.productName && batch.productName.trim()) {
            productType = batch.productName;
          } else if (batch.dungProductName && batch.dungProductName.trim()) {
            productType = batch.dungProductName;
          }

          console.log(`✅ Product Type for Batch ${batch.batchNumber}:`, productType, '| Source:', batch.finalProductTypeName);

          // Get available quantity - if not provided, use total quantity
          const availableQty = batch.availableQuantityKg !== undefined && batch.availableQuantityKg !== null
            ? batch.availableQuantityKg
            : quantityKg;

          // Use price from API response (from dung_products table), fallback to hardcoded mapping only if not available
          const apiPrice = batch.pricePerUnit !== undefined && batch.pricePerUnit !== null;
          const actualPrice = apiPrice
            ? Number(batch.pricePerUnit)
            : await this.getProductPrice(productType);
          console.log(`💰 Price for ${productType}: ₹${actualPrice} (source: ${apiPrice ? 'API/Database' : 'Fallback mapping'})`);

          return {
            id: batch.id?.toString() || `batch_${batch.batchNumber}`,
            batchNumber: batch.batchNumber || batch.batchCode || 'Unknown',
            gasType: productType.toLowerCase() as any,
            productType: productType, // Store actual product name for display
            productionDate: new Date(batch.productionStartDate || batch.createdAt || new Date()),
            expiryDate: batch.expiryDate ? new Date(batch.expiryDate) :
              batch.productionEndDate ? new Date(new Date(batch.productionEndDate).getTime() + 90 * 24 * 60 * 60 * 1000) :
              batch.createdAt ? new Date(new Date(batch.createdAt).getTime() + 90 * 24 * 60 * 60 * 1000) :
              undefined,
            quantity: quantityKg, // Total original quantity
            availableQuantity: availableQty, // Actual available after allocations
            unit: 'kg' as const,
            qualityGrade,
            purityPercentage: purity,
            quality: {
              methaneContent: purity,
              purity: purity,
              pressure: batch.h2sContentPpm || 15,
              temperature: 25
            },
            location: {
              plantId: batch.manufacturingUnitName || batch.clusterId?.toString() || 'Plant Unknown',
              tankId: batch.manufacturingUnitId?.toString() || batch.unitId?.toString(),
              coordinates: { lat: 28.7041, lng: 77.1025 } // Default Delhi coords
            },
            // Manufacturing unit information for display
            manufacturingUnitId: batch.manufacturingUnitId,
            manufacturingUnitName: batch.manufacturingUnitName,
            manufacturingUnitLocation: batch.manufacturingUnitLocation,
            pesoCompliance: {
              certificateNumber: `PESO-${new Date().getFullYear()}-${batch.id}`,
              issueDate: new Date(batch.createdAt || Date.now()),
              expiryDate: batch.expiryDate ? new Date(batch.expiryDate) :
                new Date((batch.createdAt ? new Date(batch.createdAt).getTime() : Date.now()) + 90 * 24 * 60 * 60 * 1000),
              status: 'pending',
              inspector: 'System'
            },
            pricePerUnit: actualPrice, // Use actual product price from dung_products table
            reservedQuantity: 0, // All READY_FOR_SALE are not yet allocated
            status: 'available' as const // All READY_FOR_SALE batches are 'available' for sale
          };
        }));

        console.log(`✅ Transformed ${inventory.length} mfg_production_batch records to InventoryItem format`);

        return {
          success: true,
          data: inventory,
          pagination: {
            page: 1,
            limit: inventory.length,
            total: inventory.length,
            totalPages: 1
          }
        };
      }

      // If no batches found, return empty success
      console.log('ℹ️ No READY_FOR_SALE batches found in mfg_production_batch table');
      return {
        success: true,
        data: [],
        pagination: { page: 1, limit: 0, total: 0, totalPages: 0 }
      };

    } catch (error) {
      console.error('❌ Failed to fetch READY_FOR_SALE batches from mfg_production_batch:', error);

      // NO FALLBACK - Return empty array with error
      return {
        success: false,
        data: [],
        pagination: { page: 1, limit: 0, total: 0, totalPages: 0 },
        message: `Failed to load inventory: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  },

  /**
   * Helper method to calculate biogas price based on quality and purity
   * DEPRECATED: This now should be called with cached pricing data from API
   * Kept for backward compatibility, but will be removed in future versions
   */
  calculateBioagasPrice(grade: 'A' | 'B' | 'C', purity: number): number {
    // Default fallback prices if API data not available
    // These should match the values in the backend pricing configuration
    if (grade === 'A' || purity >= 95) return 55;
    if (grade === 'B' || purity >= 85) return 45;
    return 35; // Grade C
  },

  /**
   * Get actual product price from dung_products table mapping
   * Prices sourced from: dung_products table price_per_unit column
   */
  async getProductPrice(productName: string): Promise<number> {
    // Price mapping from dung_products table
    const priceMapping: { [key: string]: number } = {
      'pellets': 21,
      'funeral logs': 20,
      'dhoop stick': 5,
      'dhoop sticks': 5,
      'agarbhathi': 2,
      'fluids': 12,
      'fertilizers': 25,
      'fertilizer': 25,
      'fertizers': 25,  // Handle typo variant from database
      'granules': 5555,
      'fields fertilizers': 20,
      'field fertilizers': 20,
      'granules s': 100.01
    };

    // Normalize product name for matching
    const normalizedName = productName.trim().toLowerCase();

    console.log(`🔍 Looking up price for product: ${productName}`);

    // Try exact match first
    if (priceMapping[normalizedName]) {
      const price = priceMapping[normalizedName];
      console.log(`✅ Found product price for "${productName}": ₹${price}`);
      return price;
    }

    // Try partial match
    for (const [key, price] of Object.entries(priceMapping)) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        console.log(`✅ Found product price (partial match) for "${productName}": ₹${price}`);
        return price;
      }
    }

    console.warn(`⚠️ Product not found in price mapping: ${productName}, using default ₹45`);
    return 45; // Fallback price if not found
  },

  /**
   * Get actual product quantities from dung_products table mapping
   * Quantities sourced from: dung_products table total_quantity and available_quantity columns
   * All units are in kg
   */
  async getProductQuantities(productName: string): Promise<{ totalQuantity: number; availableQuantity: number }> {
    try {
      console.log(`📊 Fetching real quantities for product: ${productName}`);

      // Dynamically import the functions from marketplace productsService
      const { fetchDungProductsAvailable, fetchProductionBatchQuantities } = await import('../../marketplace/services/productsService');

      // Fetch both available and total quantities from actual database
      const [availableMap, totalMap] = await Promise.all([
        fetchDungProductsAvailable(),
        fetchProductionBatchQuantities()
      ]);

      // Normalize product name for matching
      const normalizedName = productName.trim().toLowerCase();

      // Get quantities from the maps
      const availableQty = availableMap.get(normalizedName) || 0;
      const totalQty = totalMap.get(normalizedName) || 0;

      console.log(`✅ Found quantities for "${productName}": Total=${totalQty}kg, Available=${availableQty}kg`);

      return {
        totalQuantity: totalQty,
        availableQuantity: availableQty
      };
    } catch (error) {
      console.error(`❌ Error fetching product quantities: ${error}`);

      // Fallback to hardcoded values if API fails
      const fallbackMapping: { [key: string]: { total: number; available: number } } = {
        'pellets': { total: 0, available: 0 },
        'funeral logs': { total: 869, available: 869 },
        'dhoop stick': { total: 953, available: 800 },
        'dhoop sticks': { total: 953, available: 800 },
        'agarbhathi': { total: 0, available: 0 },
        'agarbati': { total: 0, available: 0 },
        'fluids': { total: 0, available: 0 },
        'fertilizers': { total: 2070, available: 1633 },
        'fertilizer': { total: 2070, available: 1633 },
        'fertizers': { total: 2070, available: 1633 },
        'granules': { total: 0, available: 0 },
        'granules 2': { total: 0, available: 0 },
        'granules s': { total: 0, available: 0 },
        'fields fertilizers': { total: 220, available: 220 },
        'field fertilizers': { total: 220, available: 220 },
      };

      const normalizedName = productName.trim().toLowerCase();

      if (fallbackMapping[normalizedName]) {
        const quantities = fallbackMapping[normalizedName];
        console.log(`⚠️ Using fallback for "${productName}": Total=${quantities.total}kg, Available=${quantities.available}kg`);
        return quantities;
      }

      console.warn(`⚠️ Product not found, using default: ${productName}`);
      return { totalQuantity: 0, availableQuantity: 0 };
    }
  },

  /**
   * Fetch dynamic pricing tiers from backend API
   * TODO: This endpoint needs to be created in Commerce Service
   */
  async getPricingTiers(): Promise<ApiResponse<any[]>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/pricing/biogas-tiers`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded pricing tiers from API');
        return {
          success: true,
          data: data.data || data.tiers || [],
          message: 'Pricing tiers retrieved successfully'
        };
      }

      // If API doesn't exist yet, return default tiers
      console.warn('⚠️ Pricing API endpoint not found, using default tiers');
      return {
        success: false,
        data: [
          { grade: 'A', minPurity: 95, pricePerUnit: 55, effectiveFrom: new Date() },
          { grade: 'B', minPurity: 85, pricePerUnit: 45, effectiveFrom: new Date() },
          { grade: 'C', minPurity: 0, pricePerUnit: 35, effectiveFrom: new Date() }
        ],
        message: 'Using default pricing tiers - API endpoint not implemented yet'
      };
    } catch (error) {
      console.error('Failed to fetch pricing tiers:', error);
      // Return default tiers as fallback
      return {
        success: false,
        data: [
          { grade: 'A', minPurity: 95, pricePerUnit: 55, effectiveFrom: new Date() },
          { grade: 'B', minPurity: 85, pricePerUnit: 45, effectiveFrom: new Date() },
          { grade: 'C', minPurity: 0, pricePerUnit: 35, effectiveFrom: new Date() }
        ],
        message: 'Failed to load pricing tiers from API'
      };
    }
  },

  async updateInventoryItem(id: string, updates: Partial<InventoryItem>): Promise<ApiResponse<InventoryItem>> {
    const inventory = generateMockInventory();
    const item = inventory.find(i => i.id === id);
    if (!item) {
      return { success: false, data: null as any, message: 'Inventory item not found' };
    }
    const updatedItem = { ...item, ...updates };
    return {
      success: true,
      data: updatedItem,
      message: 'Inventory item updated successfully'
    };
  },

  // Order Management
  async getOrders(filters?: OrderFilters): Promise<PaginatedResponse<Order>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      // Fetch from backend API
      const response = await fetch(
        `${API_BASE_URL}/api/v1/orders`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Check if response has data array
        if (data.data && Array.isArray(data.data)) {
          return {
            success: true,
            data: data.data,
            pagination: {
              page: data.pagination?.page || 1,
              limit: data.pagination?.limit || 10,
              total: data.pagination?.total || data.data.length,
              totalPages: data.pagination?.totalPages || 1
            }
          };
        }
      }

      // API returned non-200 status
      throw new Error(`API returned status ${response.status}`);
    } catch (error) {
      console.error('❌ Failed to fetch orders from backend API:', error);
      // Return empty array with error message - no mock data fallback
      return {
        success: false,
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        },
        message: `Failed to load orders: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  },

  async createOrder(order: Omit<Order, 'id' | 'orderNumber'>): Promise<ApiResponse<Order>> {
    const newOrder: Order = {
      ...order,
      id: `ord_${Date.now()}`,
      orderNumber: `ORD-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`
    };
    return {
      success: true,
      data: newOrder,
      message: 'Order created successfully'
    };
  },

  // Invoice Management
  async getInvoices(): Promise<PaginatedResponse<Invoice>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      // Fetch from backend API
      const response = await fetch(
        `${API_BASE_URL}/api/v1/invoices`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Check if response has data array
        if (data.data && Array.isArray(data.data)) {
          return {
            success: true,
            data: data.data,
            pagination: {
              page: data.pagination?.page || 1,
              limit: data.pagination?.limit || 10,
              total: data.pagination?.total || data.data.length,
              totalPages: data.pagination?.totalPages || 1
            }
          };
        }
      }

      // API returned non-200 status
      throw new Error(`API returned status ${response.status}`);
    } catch (error) {
      console.error('❌ Failed to fetch invoices from backend API:', error);
      // Return empty array with error message - no mock data fallback
      return {
        success: false,
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        },
        message: `Failed to load invoices: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  },

  async generateInvoice(orderId: string): Promise<ApiResponse<Invoice>> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      // Fetch company banking details from configuration
      const bankingDetailsResponse = await fetch(
        `${API_BASE_URL}/api/v1/company-settings/banking-details`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      let bankingDetails = {
        accountName: 'Saubhagya Energy Solutions',
        accountNumber: '',
        ifscCode: '',
        bankName: ''
      };

      if (bankingDetailsResponse.ok) {
        const data = await bankingDetailsResponse.json();
        bankingDetails = data.data || data;
        console.log('✅ Loaded banking details from API');
      } else {
        console.warn('⚠️ Could not fetch banking details from API, will use defaults');
      }

      // Create invoice with dynamic banking details
      const invoice: Invoice = {
        id: `inv_${Date.now()}`,
        invoiceNumber: `INV-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`,
        orderId,
        customerId: '',
        invoiceDate: new Date(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        status: 'draft',
        items: [],
        subtotal: 0,
        discountAmount: 0,
        taxAmount: 0,
        totalAmount: 0,
        paidAmount: 0,
        balanceAmount: 0,
        paymentTerms: 'Net 30 days',
        bankDetails: bankingDetails,
        generatedBy: 'manual'
      };

      return {
        success: true,
        data: invoice,
        message: 'Invoice generated successfully'
      };
    } catch (error) {
      console.error('Error generating invoice:', error);
      return {
        success: false,
        data: null as any,
        message: error instanceof Error ? error.message : 'Failed to generate invoice'
      };
    }
  },

  // Voice Invoicing
  async startVoiceSession(language: 'hindi' | 'english'): Promise<ApiResponse<VoiceInvoiceData>> {
    const session: VoiceInvoiceData = {
      id: `voice_${Date.now()}`,
      sessionId: `session_${Date.now()}`,
      startTime: new Date(),
      language,
      transcription: '',
      confidence: 0,
      extractedData: { items: [] },
      status: 'recording',
      validationErrors: []
    };
    return {
      success: true,
      data: session,
      message: 'Voice session started'
    };
  },

  async processVoiceData(sessionId: string, audioData: string): Promise<ApiResponse<VoiceInvoiceData>> {
    // Mock processing
    const processedData: VoiceInvoiceData = {
      id: sessionId,
      sessionId,
      startTime: new Date(Date.now() - 60000),
      endTime: new Date(),
      language: 'hindi',
      transcription: 'ग्रीन एनर्जी इंडस्ट्रीज के लिए एक हजार क्यूबिक मीटर बायो गैस, पैंतालीस रुपए प्रति यूनिट',
      confidence: 0.85,
      extractedData: {
        customerName: 'Green Energy Industries',
        customerId: 'cust_001',
        items: [
          {
            description: 'Biogas',
            quantity: 1000,
            unit: 'cubic meters',
            pricePerUnit: 45
          }
        ],
        totalAmount: 45000
      },
      status: 'completed',
      validationErrors: []
    };
    return {
      success: true,
      data: processedData,
      message: 'Voice data processed successfully'
    };
  },

  // Payment Management
  async getPayments(): Promise<PaginatedResponse<Payment>> {
    const payments: Payment[] = [];
    return {
      success: true,
      data: payments,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    };
  },

  // Delivery Management
  async getDeliveries(): Promise<PaginatedResponse<Delivery>> {
    const deliveries: Delivery[] = [];
    return {
      success: true,
      data: deliveries,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    };
  },

  // Price Management
  async getPriceData(): Promise<ApiResponse<PriceData[]>> {
    const priceData: PriceData[] = [];
    return {
      success: true,
      data: priceData,
      message: 'Price data retrieved successfully'
    };
  },

  // Compliance
  async getComplianceRecords(): Promise<PaginatedResponse<ComplianceRecord>> {
    const records: ComplianceRecord[] = [];
    return {
      success: true,
      data: records,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    };
  },

  // Order Confirmation (US-ORD-003)
  async validateOrderForConfirmation(orderId: string): Promise<any> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/orders/${orderId}/validate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Validation failed' }));
        throw new Error(error.message || `HTTP Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating order for confirmation:', error);
      throw error;
    }
  },

  async confirmOrder(orderId: string, confirmedBy: string = 'current_user'): Promise<any> {
    const config = getEnvironmentConfig();
    const API_BASE_URL = config.microservices.commerce;
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/orders/${orderId}/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ confirmedBy }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Order confirmation failed' }));

        // Handle specific error codes
        if (response.status === 409) {
          throw new Error(error.message || 'Inventory or credit unavailable. Please check order details.');
        } else if (response.status === 402) {
          throw new Error(error.message || 'Payment required. Insufficient credit.');
        } else if (response.status === 404) {
          throw new Error(error.message || 'Order not found.');
        } else if (response.status === 400) {
          throw new Error(error.message || 'Invalid order status. Only PENDING orders can be confirmed.');
        }

        throw new Error(error.message || `HTTP Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error confirming order:', error);
      throw error;
    }
  },

  async getInvoicePreview(invoiceId: string): Promise<any> {
    // This would call backend API: GET /api/v1/invoices/{id}/preview
    return {
      invoiceNumber: 'INV-2024-000123',
      invoiceDate: new Date(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      orderNumber: 'ORD-2024-001',
      customerName: 'Green Energy Industries',
      customerAddress: '123 Industrial Area, Phase 2, Noida, UP 201301',
      customerGST: '09ABCDE1234F1Z5',
      items: [
        {
          description: 'Biogas Supply - BG-2024-001',
          quantity: 1000,
          unit: 'cubic meters',
          pricePerUnit: 45,
          amount: 45000,
        },
      ],
      subtotal: 45000,
      discountAmount: 2250,
      taxableAmount: 42750,
      cgst: 3847.50,
      sgst: 3847.50,
      igst: 0,
      totalAmount: 50445,
      paymentTerms: 'Net 30 days',
      notes: 'Payment due within 30 days of invoice date',
    };
  },

  // Dashboard Metrics
  async getSalesMetrics(): Promise<ApiResponse<SalesMetrics>> {
    const metrics: SalesMetrics = {
      totalRevenue: 15000000,
      totalOrders: 105,
      averageOrderValue: 142857,
      totalCustomers: 25,
      activeContracts: 8,
      inventoryValue: 2500000,
      pendingPayments: 750000,
      deliverySuccess: 98.5,
      customerSatisfaction: 4.6,
      periodComparison: {
        revenue: { current: 15000000, previous: 12000000, change: 25 },
        orders: { current: 105, previous: 85, change: 23.5 },
        customers: { current: 25, previous: 20, change: 25 }
      }
    };
    return {
      success: true,
      data: metrics,
      message: 'Metrics retrieved successfully'
    };
  },

  // NEW: Sales Dashboard Summary with Real API Data from Backend
  async getDashboardSummary(startDate?: string, endDate?: string): Promise<any> {
    try {
      // 1. Set default to Last 7 days if not provided
      const end = endDate || new Date().toISOString().split('T')[0];
      const start = startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      console.log('📊 Fetching dashboard metrics for range:', start, 'to', end);

      const token = localStorage.getItem('saubhagya_jwt_token') || '';
      const config = getEnvironmentConfig();
      const API_BASE_URL = config.microservices.commerce;

      // Cache busting timestamp
      const cacheBustParam = `_t=${Date.now()}`;

      // ====== GET CURRENT USER'S SALES UNITS ======
      let userSalesUnitIds: number[] = [];
      const isAdmin = salesService.isCurrentUserAdmin();

      if (!isAdmin) {
        const salesUnitsResponse = await salesService.getCurrentUserSalesUnits();
        if (salesUnitsResponse.success && salesUnitsResponse.salesUnitIds.length > 0) {
          userSalesUnitIds = salesUnitsResponse.salesUnitIds;
          console.log(`👤 Non-admin user has access to ${userSalesUnitIds.length} sales units:`, userSalesUnitIds);
        } else {
          console.warn('⚠️ Could not fetch user sales units, will show all data');
        }
      } else {
        console.log('👑 Admin user - showing all data across all sales units');
      }

      // Helper function for fetch with timeout (10 seconds)
      const fetchWithTimeout = async (url: string, options: RequestInit, timeoutMs: number = 10000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        try {
          const response = await fetch(url, { ...options, signal: controller.signal });
          clearTimeout(timeoutId);
          return response.json();
        } catch (error: any) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            console.warn(`⏱️ Request timeout (${timeoutMs}ms): ${url}`);
          }
          throw error;
        }
      };

      // Skip the slow dashboard/metrics endpoint - use fast endpoints only
      console.log('📊 Skipping slow dashboard/metrics endpoint, using fast endpoints');
      const dashboardMetricsRes = { success: false, data: null };

      // Build sales unit parameter if applicable
      const salesUnitParam = userSalesUnitIds.length > 0 ? `&salesUnitIds=${userSalesUnitIds.join(',')}` : '';

      // Fetch only fast endpoints with timeout (10 seconds each)
      const [ordersRes, customersRes, inventoryRes] = await Promise.all([
        fetchWithTimeout(
          `${API_BASE_URL}/api/v1/orders?page=0&size=100&sortBy=createdAt&sortDir=desc${salesUnitParam}&${cacheBustParam}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
          10000
        ).catch(e => {
          console.warn('Orders API error/timeout:', e.message);
          return { success: false, data: { content: [] } };
        }),

        fetchWithTimeout(
          `${API_BASE_URL}/api/v1/customers?page=0&size=100${salesUnitParam}&${cacheBustParam}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
          10000
        ).catch(e => {
          console.warn('Customers API error/timeout:', e.message);
          return { success: false, data: { content: [] } };
        }),

        fetchWithTimeout(
          `${API_BASE_URL}/api/v1/mfg-production-batches/ready-for-sale?${cacheBustParam}`,
          { headers: { 'Content-Type': 'application/json' } },
          10000
        ).catch(e => {
          console.warn('Inventory API error/timeout:', e.message);
          return { success: false, data: [] };
        })
      ]);

      // Extract metrics from the new endpoint (will be recalculated below with real data)
      const backendMetrics = dashboardMetricsRes.data || {};
      let metrics = {
        totalRevenue: 0, // Will be calculated from actual orders below
        totalOrders: backendMetrics.totalOrders || 0,  // Will be overridden with real API total below
        averageOrderValue: 0, // Will be calculated from actual revenue and orders below
        totalCustomers: backendMetrics.totalCustomers || 0,
        newCustomers: backendMetrics.totalCustomers || 0,
        activeContracts: 0,
        pendingOrders: 0,
        conversionRate: 0
      };

      console.log('✅ Dashboard metrics from backend:', metrics);

      // Extract orders and customers - Handle multiple response formats (FIXED)
      let orders = [];
      let customers = [];
      let totalOrdersCount = 0;  // Store total orders count from API pagination
      let totalCustomersCount = 0;  // Store total customers count from API pagination

      console.log('🔍 DEBUGGING - Full API Responses:');
      console.log('   Orders Response:', JSON.stringify(ordersRes, null, 2).substring(0, 500));
      console.log('   Customers Response:', JSON.stringify(customersRes, null, 2).substring(0, 500));

      // Try multiple ways to extract orders data and get total count
      if (ordersRes && ordersRes.data) {
        if (Array.isArray(ordersRes.data)) {
          orders = ordersRes.data;
          totalOrdersCount = orders.length;
          console.log('✅ Orders extracted as direct array, count:', orders.length);
        } else if (ordersRes.data.content && Array.isArray(ordersRes.data.content)) {
          orders = ordersRes.data.content;
          totalOrdersCount = ordersRes.data.totalElements || orders.length;  // Get real total from API pagination
          console.log('✅ Orders extracted from .content, count:', orders.length, 'Total from API:', totalOrdersCount);
        } else if (ordersRes.data.data && Array.isArray(ordersRes.data.data)) {
          orders = ordersRes.data.data;
          totalOrdersCount = orders.length;
          console.log('✅ Orders extracted from .data, count:', orders.length);
        } else if (ordersRes.data.orders && Array.isArray(ordersRes.data.orders)) {
          orders = ordersRes.data.orders;
          totalOrdersCount = orders.length;
          console.log('✅ Orders extracted from .orders, count:', orders.length);
        } else {
          console.warn('⚠️ Could not extract orders array, ordersRes.data structure:', Object.keys(ordersRes.data || {}));
        }
      } else {
        console.warn('⚠️ ordersRes is missing or has no data property', { ordersRes });
      }

      // Override metrics.totalOrders with the real API total (not the dashboard endpoint's calculated value)
      if (totalOrdersCount > 0) {
        metrics.totalOrders = totalOrdersCount;
        console.log('📊 Updated metrics.totalOrders from API pagination total:', totalOrdersCount);
      }

      // Try multiple ways to extract customers data and get total count
      if (customersRes && customersRes.data) {
        if (Array.isArray(customersRes.data)) {
          customers = customersRes.data;
          totalCustomersCount = customers.length;
          console.log('✅ Customers extracted as direct array, count:', customers.length);
        } else if (customersRes.data.content && Array.isArray(customersRes.data.content)) {
          customers = customersRes.data.content;
          totalCustomersCount = customersRes.data.totalElements || customers.length;  // Get real total from API pagination
          console.log('✅ Customers extracted from .content, count:', customers.length, 'Total from API:', totalCustomersCount);
        } else if (customersRes.data.data && Array.isArray(customersRes.data.data)) {
          customers = customersRes.data.data;
          totalCustomersCount = customersRes.data.totalElements || customers.length;
          console.log('✅ Customers extracted from .data, count:', customers.length, 'Total from API:', totalCustomersCount);
        } else if (customersRes.data.customers && Array.isArray(customersRes.data.customers)) {
          customers = customersRes.data.customers;
          totalCustomersCount = customersRes.data.totalElements || customers.length;
          console.log('✅ Customers extracted from .customers, count:', customers.length, 'Total from API:', totalCustomersCount);
        } else {
          console.warn('⚠️ Could not extract customers array, customersRes.data structure:', Object.keys(customersRes.data || {}));
        }
      } else {
        console.warn('⚠️ customersRes is missing or has no data property', { customersRes });
      }

      // Update metrics.totalCustomers with the real API total (not just the page size)
      if (totalCustomersCount > 0) {
        metrics.totalCustomers = totalCustomersCount;
        metrics.newCustomers = totalCustomersCount;
        console.log('📊 Updated metrics.totalCustomers from API pagination total:', totalCustomersCount);
      }

      // Extract inventory
      const inventory = Array.isArray(inventoryRes.data) ? inventoryRes.data :
                       (inventoryRes.data && inventoryRes.data.content ? inventoryRes.data.content : []);

      console.log('📦 EXTRACTED DATA Summary:', {
        ordersCount: orders.length,
        customersCount: customers.length,
        inventoryCount: inventory.length,
        orderSample: orders.length > 0 ? orders[0] : 'NO ORDERS',
        customerSample: customers.length > 0 ? customers[0] : 'NO CUSTOMERS'
      });

      // 3. Aggregate chart data
      let revenueData = this.aggregateRevenueByDate(orders, start, end, totalOrdersCount);
      let orderStatusData = this.groupOrdersByStatus(orders);
      let customerGrowthData = this.calculateCustomerGrowth(customers, start, end);
      const inventoryAlerts = this.identifyInventoryAlerts(inventory);

      // Check if we got actual data
      const revenueTotal = revenueData.reduce((sum, d) => sum + d.revenue, 0);
      const orderStatusTotal = orderStatusData.reduce((sum, d) => sum + d.count, 0);
      const hasOrderStatus = orderStatusData && orderStatusData.length > 0;
      const hasCustomerGrowth = customerGrowthData && customerGrowthData.length > 0;

      // FIX: Update metrics.totalRevenue to use the ACTUAL calculated revenue from aggregated data
      // This ensures the Total Revenue card shows the correct value matching the Revenue Trend chart
      metrics.totalRevenue = revenueTotal;

      // FIX: Recalculate averageOrderValue based on actual revenue and order count
      metrics.averageOrderValue = metrics.totalOrders > 0
        ? Math.round((metrics.totalRevenue / metrics.totalOrders) * 100) / 100
        : 0;

      console.log('✅ FIXED: Updated metrics with actual calculated values:', {
        totalRevenue: {
          previousValue: backendMetrics.totalRevenue || 0,
          newValue: revenueTotal,
          difference: (backendMetrics.totalRevenue || 0) - revenueTotal
        },
        averageOrderValue: {
          calculated: metrics.averageOrderValue,
          basedOn: `₹${metrics.totalRevenue} ÷ ${metrics.totalOrders} orders`
        }
      });

      console.log('📊 Data Validation:', {
        revenueTotal,
        orderStatusTotal,
        metricsTotal: metrics.totalOrders,
        discrepancy: Math.abs(orderStatusTotal - metrics.totalOrders)
      });

      // If order status total doesn't match metrics, adjust it using Largest Remainder Method
      // This ensures the Order Status chart total matches the metric card total exactly
      if (orderStatusTotal !== metrics.totalOrders && orderStatusTotal > 0) {
        console.warn(`⚠️ Order status mismatch: ${orderStatusTotal} vs ${metrics.totalOrders}, normalizing...`);
        // Use Largest Remainder Method to distribute counts proportionally while ensuring exact total
        const scaleFactor = metrics.totalOrders / orderStatusTotal;

        // Calculate quotas (integer part + fractional part)
        const quotas = orderStatusData.map(item => ({
          status: item.status,
          totalValue: item.totalValue,
          quota: item.count * scaleFactor,
          integer: Math.floor(item.count * scaleFactor),
          remainder: (item.count * scaleFactor) % 1
        }));

        // Sum of integer parts
        const integerSum = quotas.reduce((sum, q) => sum + q.integer, 0);
        const remainder = metrics.totalOrders - integerSum;

        // Sort by remainder (descending) and add 1 to top 'remainder' items
        quotas.sort((a, b) => b.remainder - a.remainder);

        // Assign counts with proper distribution
        orderStatusData = quotas.map((item, index) => ({
          status: item.status,
          totalValue: item.totalValue,
          count: item.integer + (index < remainder ? 1 : 0)
        }));

        console.log('✅ Order status normalized to match metrics.totalOrders exactly');
      }

      // IMPORTANT: Always use real data when we have orders, even if revenue is 0
      // Only use sample data if we have NO real data at all
      const hasRealOrderData = orders.length > 0;

      if (!hasOrderStatus || !hasCustomerGrowth) {
        console.warn('⚠️ Missing aggregated data, generating sample data for visualization...');
        if (!hasOrderStatus) {
          orderStatusData = this.generateSampleOrderStatus();
          console.log('📊 Using sample order status data');
        }
        if (!hasCustomerGrowth) {
          customerGrowthData = this.generateSampleCustomerGrowth(start, end);
          console.log('📊 Using sample customer growth data');
        }
      }

      // For revenue: ALWAYS use real aggregated data (don't replace with sample)
      // Even if revenueTotal is 0, it's REAL data that shows no revenue yet
      console.log('✅ Using REAL revenue data (not sample):', {
        totalOrders: orders.length,
        revenueTotal,
        dataPoints: revenueData.length
      });

      // Generate sample recent orders if we don't have real ones
      let recentOrdersList = orders.slice(0, 5);
      if (recentOrdersList.length === 0) {
        console.log('📊 Generating sample recent orders');
        recentOrdersList = [
          { id: 1, orderNumber: 'ORD-001', status: 'CONFIRMED', finalAmount: 15000, createdAt: new Date().toISOString() },
          { id: 2, orderNumber: 'ORD-002', status: 'DELIVERED', finalAmount: 22500, createdAt: new Date().toISOString() },
          { id: 3, orderNumber: 'ORD-003', status: 'PENDING', finalAmount: 18000, createdAt: new Date().toISOString() },
          { id: 4, orderNumber: 'ORD-004', status: 'DISPATCHED', finalAmount: 25000, createdAt: new Date().toISOString() },
          { id: 5, orderNumber: 'ORD-005', status: 'CONFIRMED', finalAmount: 19500, createdAt: new Date().toISOString() }
        ];
      }

      // Fetch real marketplace data from APIs (actual product and inquiry counts)
      let marketplaceMetricsData: any = {
        totalProducts: 0,
        activeProducts: 0,
        totalInquiries: 0,
        convertedInquiries: 0,
        conversionRate: 0
      };

      try {
        // Fetch products count from marketplace API
        // Note: This API returns raw Spring Page object (not wrapped in {success, data})
        const productsRes = await fetch(
          `${API_BASE_URL}/api/v1/marketplace/products?page=0&pageSize=1&${cacheBustParam}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        ).then(r => r.json()).catch(e => {
          console.warn('Products API error:', e);
          return { totalElements: 0 };
        });

        // Fetch all inquiries to get total and find contacted persons
        // Note: This API returns raw Spring Page object (not wrapped in {success, data})
        const inquiriesRes = await fetch(
          `${API_BASE_URL}/api/v1/marketplace/inquiries?page=0&size=1000&${cacheBustParam}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        ).then(r => r.json()).catch(e => {
          console.warn('Inquiries API error:', e);
          return { totalElements: 0, content: [] };
        });

        // Extract totalElements directly (APIs return raw Spring Page object)
        const totalProducts = productsRes?.totalElements || 0;
        const totalInquiries = inquiriesRes?.totalElements || 0;

        // Filter all inquiries to find contacted persons (client-side filtering)
        const allInquiriesData = inquiriesRes?.content || [];
        const contactRecords = allInquiriesData.filter(inq => {
          const status = (inq.status || '').toString().toUpperCase().trim();
          // Match common variations of contacted status
          return status === 'CONTACTED' || status === 'CONTACT' || status.includes('CONTACTED');
        });
        const totalContactedPersons = contactRecords.length;

        // Debug logging
        console.log('📋 Inquiries Status Breakdown:', {
          totalInquiriesFromAPI: totalInquiries,
          allInquiriesRetrieved: allInquiriesData.length,
          filteredAsContacted: totalContactedPersons,
          statusValuesFound: [...new Set(allInquiriesData.map(inq => inq.status))],
          sampleRecords: allInquiriesData.slice(0, 3).map(inq => ({ customerName: inq.customerName, status: inq.status }))
        });

        // Count converted inquiries (confirmed orders)
        const confirmedOrders = orders.filter(o => (o.status || '').toUpperCase() === 'CONFIRMED');
        const conversionRate = totalContactedPersons > 0 ? Math.round((confirmedOrders.length / totalContactedPersons) * 100) : 0;

        marketplaceMetricsData = {
          totalProducts: totalProducts,
          activeProducts: Math.max(Math.ceil(totalProducts * 0.8), 0), // 80% active rate
          totalInquiries: totalInquiries,  // Total inquiries across all statuses
          totalInquiriesContacted: totalContactedPersons,  // Contacted persons (with "contacted" status)
          convertedInquiries: confirmedOrders.length,
          conversionRate: conversionRate
        };

        console.log('📊 Marketplace Metrics (Inquiries breakdown):', {
          totalInquiriesAllStatus: `${totalInquiries} (all statuses)`,
          totalContactedPersons: `${totalContactedPersons} (with "contacted" status)`,
          totalProducts,
          contactRecordsRetrieved: contactRecords.length,
          confirmedOrdersCount: confirmedOrders.length,
          conversionRate: `${conversionRate}%`,
          marketplaceMetricsData
        });
      } catch (err) {
        console.warn('⚠️ Error fetching marketplace metrics from APIs:', err);
        // Fallback: use 0 since we cannot fetch contacted persons without the API
        const confirmedOrders = orders.filter(o => (o.status || '').toUpperCase() === 'CONFIRMED');

        marketplaceMetricsData = {
          totalProducts: 0,
          activeProducts: 0,
          totalInquiries: 0,  // Cannot determine without API
          totalInquiriesContacted: 0,  // Cannot determine without API
          convertedInquiries: confirmedOrders.length,
          conversionRate: 0
        };
        console.log('⚠️ Marketplace metrics fallback (API error):', marketplaceMetricsData);
      }

      console.log('✅ Dashboard data ready:', {
        metrics,
        revenueDataPoints: revenueData.length,
        revenueTotal: revenueData.reduce((sum, d) => sum + d.revenue, 0),
        orderStatusCount: orderStatusData.length,
        customerGrowthPoints: customerGrowthData.length,
        dataSource: {
          hasRealOrders: orders.length > 0,
          hasRealCustomers: customers.length > 0,
          usingSampleData: revenueTotal === 0
        },
        inventory: { batches: inventory.length, alerts: inventoryAlerts.length }
      });

      return {
        success: true,
        data: {
          metrics,
          revenueData,
          orderStatusData,
          customerGrowthData,
          marketplaceMetrics: marketplaceMetricsData,
          recentOrders: recentOrdersList,
          inventoryAlerts,
          inventoryBatches: inventory,
          dateRange: { startDate: start, endDate: end }
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  },

  // Helper: Calculate metrics from orders and customers
  calculateDashboardMetrics(orders: any[], customers: any[], contracts: any[]): any {
    const totalRevenue = orders.reduce((sum, order) => sum + (order.finalAmount || 0), 0);
    const totalOrders = orders.length;
    const averageOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;
    const totalCustomers = customers.length;

    // Count pending orders
    const pendingOrders = orders.filter(o => o.status === 'PENDING' || o.status === 'pending').length;

    return {
      totalRevenue,
      totalOrders,
      averageOrderValue,
      totalCustomers,
      newCustomers: customers.length,
      activeContracts: contracts.length,
      pendingOrders,
      conversionRate: 0
    };
  },

  // Helper: Generate sample data for demo/development
  generateSampleRevenueData(startDate: string, endDate: string): any[] {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const data: any[] = [];

    for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
      const dateKey = d.toISOString().split('T')[0];
      // Generate random revenue between 5K and 50K
      const revenue = Math.floor(Math.random() * 45000 + 5000);
      const orderCount = Math.floor(Math.random() * 10 + 1);
      data.push({ date: dateKey, revenue, orderCount });
    }
    return data;
  },

  generateSampleCustomerGrowth(startDate: string, endDate: string): any[] {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const data: any[] = [];
    let totalCustomers = 25;

    for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
      const dateKey = d.toISOString().split('T')[0];
      const newCustomers = Math.floor(Math.random() * 5);
      totalCustomers += newCustomers;
      data.push({ date: dateKey, newCustomers, totalCustomers });
    }
    return data;
  },

  generateSampleOrderStatus(): any[] {
    return [
      { status: 'CONFIRMED', count: 45, totalValue: 125500 },
      { status: 'PENDING', count: 12, totalValue: 35200 },
      { status: 'DELIVERED', count: 28, totalValue: 98750 },
      { status: 'DISPATCHED', count: 8, totalValue: 22100 }
    ];
  },

  // Helper: Aggregate revenue by date
  aggregateRevenueByDate(orders: any[], startDate: string, endDate: string, totalOrdersCount: number = 0): any[] {
    const revenueMap = new Map<string, { revenue: number; count: number }>();

    // Fill all dates in range with 0
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T23:59:59');

    console.log(`💰 Revenue Aggregation Started:`, {
      totalOrders: orders.length,
      startDate,
      endDate,
      startDateObj: start.toISOString(),
      endDateObj: end.toISOString()
    });

    const initializedDates: string[] = [];
    for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
      const dateKey = d.toISOString().split('T')[0];
      revenueMap.set(dateKey, { revenue: 0, count: 0 });
      initializedDates.push(dateKey);
    }

    console.log(`📅 Initialized date range:`, {
      dateCount: initializedDates.length,
      firstDate: initializedDates[0],
      lastDate: initializedDates[initializedDates.length - 1]
    });

    // Aggregate orders by date
    const aggregatedOrders: any[] = [];
    const failedOrders: any[] = [];
    let successfullyProcessedOrders = 0;

    orders.forEach((order, index) => {
      // Try multiple date field names that might exist (handles both CONFIRMED and other statuses)
      const dateField = order.orderDate || order.createdAt || order.orderCreatedAt || order.date || order.createdOn;
      if (dateField) {
        try {
          // Parse date properly
          const orderDate = typeof dateField === 'string' ? new Date(dateField) : new Date(dateField);
          const dateKey = orderDate.toISOString().split('T')[0];

          // Include orders with ANY status - even if revenue is 0
          // This shows ALL order activity in the chart
          const status = order.status || 'UNKNOWN';
          const revenue = order.finalAmount || order.totalAmount || order.amount || 0;

          if (revenueMap.has(dateKey)) {
            const current = revenueMap.get(dateKey)!;
            revenueMap.set(dateKey, {
              revenue: current.revenue + revenue,
              count: current.count + 1
            });
            aggregatedOrders.push({ dateKey, revenue, status, found: true, orderId: order.id });
            successfullyProcessedOrders++;

            // Log every 10th order to avoid spam
            if (successfullyProcessedOrders % 10 === 0) {
              console.log(`✅ [${successfullyProcessedOrders}] Order aggregated - Date: ${dateKey}, Revenue: ₹${revenue}, Status: ${status}`);
            }
          } else if (!revenueMap.has(dateKey)) {
            failedOrders.push({
              orderDate: dateField,
              dateKey,
              reason: 'Date not in range',
              revenue,
              status,
              orderId: order.id
            });
          }
        } catch (e) {
          failedOrders.push({
            orderDate: dateField,
            orderId: order.id,
            reason: 'Date parsing error: ' + (e instanceof Error ? e.message : String(e)),
            error: e
          });
        }
      } else {
        failedOrders.push({
          orderId: order.id,
          status: order.status,
          reason: 'No date field found in order',
          availableFields: Object.keys(order).filter(k => k.includes('date') || k.includes('Date') || k.includes('time') || k.includes('Time'))
        });
      }
    });

    // Calculate aggregation statistics
    const statusDistribution = aggregatedOrders.reduce((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const failureReasons = failedOrders.reduce((acc, f) => {
      acc[f.reason] = (acc[f.reason] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    console.log(`📊 Revenue Aggregation Result:`, {
      totalOrders: orders.length,
      successfullyAggregated: aggregatedOrders.length,
      failedToAggregate: failedOrders.length,
      successRate: `${((aggregatedOrders.length / Math.max(orders.length, 1)) * 100).toFixed(1)}%`,
      statusDistribution,
      failureReasons,
      totalRevenueFromOrders: aggregatedOrders.reduce((sum, o) => sum + o.revenue, 0),
      ordersWithRevenue: aggregatedOrders.filter(o => o.revenue > 0).length,
      ordersWithoutRevenue: aggregatedOrders.filter(o => o.revenue === 0).length
    });

    if (aggregatedOrders.length === 0 && orders.length > 0) {
      console.warn(`⚠️ WARNING: No orders were aggregated!`, {
        totalOrdersCount: orders.length,
        firstOrder: orders[0],
        lastOrder: orders[orders.length - 1],
        allStatuses: [...new Set(orders.map(o => o.status))],
        failedOrdersSample: failedOrders.slice(0, 3),
        possibleIssues: [
          '1. All orders are outside the date range',
          '2. Date fields have different names than expected',
          '3. Orders have no valid date fields at all',
          '4. Revenue/Amount fields are all 0'
        ]
      });
    } else if (failedOrders.length > 0) {
      console.warn(`⚠️ ${failedOrders.length} orders could not be aggregated:`, {
        failureBreakdown: failureReasons,
        sampleFailedOrders: failedOrders.slice(0, 3)
      });
    }

    // Convert to array and sort by date
    const result = Array.from(revenueMap.entries())
      .map(([date, data]) => ({
        date,
        revenue: Math.round(data.revenue * 100) / 100, // Round to 2 decimals
        orderCount: data.count
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Calculate actual total revenue from aggregated data
    const totalRevenueFromOrders = result.reduce((sum, item) => sum + item.revenue, 0);
    // Use the passed totalOrdersCount (from API pagination) instead of just daily sum
    // This ensures we show the REAL total of all orders, not just those with revenue data
    const effectiveTotalOrderCount = totalOrdersCount > 0 ? totalOrdersCount : result.reduce((sum, item) => sum + item.orderCount, 0);

    // Summary statistics
    const nonZeroDataPoints = result.filter(r => r.revenue > 0).length;
    const zeroRevenueDataPoints = result.filter(r => r.revenue === 0 && r.orderCount > 0).length;
    const totalDataPoints = result.length;

    console.log(`📈 Final Revenue Data Points:`, {
      totalDataPoints,
      dateRange: `${result[0]?.date} to ${result[result.length - 1]?.date}`,
      nonZeroRevenuePoints: nonZeroDataPoints,
      zeroRevenueButHasOrders: zeroRevenueDataPoints,
      emptyDatePoints: totalDataPoints - nonZeroDataPoints - zeroRevenueDataPoints,
      totalRevenue: `₹${totalRevenueFromOrders.toFixed(2)}`,
      totalOrderCount: effectiveTotalOrderCount,
      apiTotalOrdersCount: totalOrdersCount,
      dailySumOrderCount: result.reduce((sum, item) => sum + item.orderCount, 0),
      averageRevenuePerDay: nonZeroDataPoints > 0 ? `₹${(totalRevenueFromOrders / nonZeroDataPoints).toFixed(2)}` : '₹0',
      sample: result.slice(0, 3)
    });

    console.log(`✅ Revenue chart ready to display with ${totalDataPoints} date points`);
    if (totalRevenueFromOrders === 0 && result.reduce((sum, r) => sum + r.orderCount, 0) > 0) {
      console.log(`💡 NOTE: Orders exist but have 0 revenue. This is normal for draft/pending orders.`);
    }

    // Add metadata to result array for use in dashboard
    (result as any).totalRevenue = totalRevenueFromOrders;
    (result as any).totalOrderCount = effectiveTotalOrderCount;

    return result;
  },

  // Helper: Group orders by status
  groupOrdersByStatus(orders: any[]): any[] {
    const statusMap = new Map<string, { count: number; totalValue: number }>();

    console.log(`📊 Order Status Grouping Started:`, {
      totalOrders: orders.length,
      statusSample: orders.map(o => o.status).slice(0, 5)
    });

    orders.forEach(order => {
      const status = (order.status || 'UNKNOWN').toUpperCase();
      const value = order.finalAmount || order.totalAmount || 0;

      if (!statusMap.has(status)) {
        statusMap.set(status, { count: 0, totalValue: 0 });
      }
      const current = statusMap.get(status)!;
      statusMap.set(status, {
        count: current.count + 1,
        totalValue: current.totalValue + value
      });
    });

    const result = Array.from(statusMap.entries())
      .map(([status, data]) => ({
        status,
        count: data.count,
        totalValue: Math.round(data.totalValue * 100) / 100
      }))
      .sort((a, b) => b.count - a.count);

    console.log(`✅ Order Status Grouping Result:`, {
      statusGroups: result.length,
      totalOrders: result.reduce((sum, item) => sum + item.count, 0),
      totalValue: result.reduce((sum, item) => sum + item.totalValue, 0),
      data: result
    });

    return result;
  },

  // Helper: Calculate customer growth with proper baseline
  calculateCustomerGrowth(customers: any[], startDate: string, endDate: string): any[] {
    const growthMap = new Map<string, { newCustomers: number; totalCustomers: number }>();

    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T23:59:59');

    console.log(`📈 Customer Growth Analysis Started:`, {
      totalCustomers: customers.length,
      startDate,
      endDate
    });

    // Calculate baseline: customers registered BEFORE the start date
    let baselineCustomers = 0;
    const customersBeforeStart: any[] = [];
    const customersInRange: any[] = [];
    const customersWithoutDate: any[] = [];

    customers.forEach(customer => {
      // Try multiple date field names
      const dateField = customer.createdAt || customer.registrationDate || customer.created_at || customer.dateRegistered || customer.createdOn;
      if (!dateField) {
        customersWithoutDate.push(customer);
        return;
      }

      try {
        const customerDate = typeof dateField === 'string' ? new Date(dateField) : new Date(dateField);
        if (customerDate < start) {
          baselineCustomers++;
          customersBeforeStart.push(customer);
        } else if (customerDate >= start && customerDate <= end) {
          customersInRange.push(customer);
        }
      } catch (e) {
        console.warn(`Failed to parse customer date: ${dateField}`, e);
        customersWithoutDate.push(customer);
      }
    });

    console.log(`📊 Customer Classification:`, {
      totalCustomers: customers.length,
      baselineCustomers,
      customersInRange: customersInRange.length,
      customersWithoutDate: customersWithoutDate.length
    });

    // Fill all dates in range with baseline
    const initializedDates: string[] = [];
    for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
      const dateKey = d.toISOString().split('T')[0];
      growthMap.set(dateKey, { newCustomers: 0, totalCustomers: baselineCustomers });
      initializedDates.push(dateKey);
    }

    console.log(`📅 Initialized dates for growth:`, {
      dateCount: initializedDates.length,
      firstDate: initializedDates[0],
      lastDate: initializedDates[initializedDates.length - 1]
    });

    // Count customers registered on each date within the range
    customersInRange.forEach(customer => {
      const dateField = customer.createdAt || customer.registrationDate || customer.created_at || customer.dateRegistered || customer.createdOn;
      if (!dateField) return;

      try {
        const dateKey = typeof dateField === 'string' ? dateField.split('T')[0] : new Date(dateField).toISOString().split('T')[0];
        if (growthMap.has(dateKey)) {
          const current = growthMap.get(dateKey)!;
          current.newCustomers += 1;
        }
      } catch (e) {
        console.warn(`Failed to aggregate customer date: ${dateField}`, e);
      }
    });

    // Calculate cumulative totals starting from baseline
    let cumulative = baselineCustomers;
    const result = Array.from(growthMap.entries()).map(([date, data]) => {
      cumulative += data.newCustomers;
      return {
        date,
        newCustomers: data.newCustomers,
        totalCustomers: cumulative
      };
    });

    const sortedResult = result.sort((a, b) => a.date.localeCompare(b.date));

    console.log(`✅ Customer Growth Result:`, {
      dataPoints: sortedResult.length,
      startingTotal: sortedResult[0]?.totalCustomers || 0,
      endingTotal: sortedResult[sortedResult.length - 1]?.totalCustomers || 0,
      totalGrowth: (sortedResult[sortedResult.length - 1]?.totalCustomers || 0) - (sortedResult[0]?.totalCustomers || 0),
      sample: sortedResult.slice(0, 3)
    });

    return sortedResult;
  },

  // Helper: Identify inventory alerts from real backend data
  identifyInventoryAlerts(inventory: any[]): any[] {
    const alerts: any[] = [];

    inventory.forEach(batch => {
      const available = batch.availableQuantity || 0;
      const total = batch.initialQuantity || 0;
      const stockLevel = batch.stockLevel || 'NORMAL';
      const daysRemaining = batch.daysRemaining || 0;
      const batchNumber = batch.batchNumber || 'Unknown';
      const productType = batch.productType || 'BIO_CNG';

      // Critical stock alert (backend already calculates this: <= 10%)
      if (stockLevel === 'CRITICAL' && available > 0) {
        alerts.push({
          batchNumber,
          gasType: productType,
          availableQuantity: available,
          alertType: 'low_stock',
          alertMessage: `Critical stock: Only ${Math.round(available)} kg remaining (${Math.round((available / total) * 100)}% of capacity)`
        });
      }

      // Low stock alert (backend already calculates this: <= 20%)
      if (stockLevel === 'LOW' && available > 0) {
        alerts.push({
          batchNumber,
          gasType: productType,
          availableQuantity: available,
          alertType: 'low_stock',
          alertMessage: `Low stock: ${Math.round(available)} kg remaining (${Math.round((available / total) * 100)}% of capacity)`
        });
      }

      // Expiring soon alert (less than 30 days)
      if (daysRemaining > 0 && daysRemaining < 30) {
        alerts.push({
          batchNumber,
          gasType: productType,
          availableQuantity: available,
          alertType: daysRemaining < 7 ? 'peso_expiry' : 'expiring_soon',
          alertMessage: daysRemaining < 7
            ? `Critical: Expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} - immediate action required`
            : `Expires in ${daysRemaining} days`
        });
      }

      // Already expired
      if (daysRemaining < 0) {
        alerts.push({
          batchNumber,
          gasType: productType,
          availableQuantity: available,
          alertType: 'peso_expiry',
          alertMessage: `Expired ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} ago - remove from inventory`
        });
      }
    });

    console.log(`📊 Generated ${alerts.length} inventory alerts from ${inventory.length} batches`);

    // Sort by severity: peso_expiry (critical) > expiring_soon > low_stock
    const severityOrder = { 'peso_expiry': 1, 'expiring_soon': 2, 'low_stock': 3 };
    alerts.sort((a, b) => (severityOrder[a.alertType as keyof typeof severityOrder] || 999) - (severityOrder[b.alertType as keyof typeof severityOrder] || 999));

    return alerts.slice(0, 5); // Limit to top 5 most critical alerts
  }
};

export default salesService;